import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import Navigation from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { logger } from '@/utils/logger';
import DiscoveryFiltersSheet from '@/pages/discovery/DiscoveryFiltersSheet';
import DiscoveryTabs from '@/pages/discovery/DiscoveryTabs';
import DiscoveryGridView from '@/pages/discovery/DiscoveryGridView';
import DiscoveryMapView from '@/pages/discovery/DiscoveryMapView';
import {
  Search,
  Users,
  Building2,
  Tent,
  ShoppingBag,
  Calendar,
  SlidersHorizontal,
  Trees,
  Backpack,
  Landmark,
  Sun,
  Map,
  LayoutGrid,
  Locate,
  Route,
} from 'lucide-react';

import { useUserLocation } from '@/hooks/useUserLocation';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Discovery = () => {
  const { token, user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('type') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [entities, setEntities] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState(searchParams.get('view') || 'grid'); // 'grid' or 'map'
  
  // Map view: geocoded location target ({lat, lng, zoom, label})
  const [mapFlyTo, setMapFlyTo] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  
  // Advanced filters
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [minExperience, setMinExperience] = useState(0);
  const [maxExperience, setMaxExperience] = useState(50);

  // "Near Me" — requests location ON DEMAND (App-Review-safe permission timing).
  // When `coords` is set, the entity list is haversine-sorted by distance
  // and entities without coordinates are dropped.
  const { coords: userCoords, state: locState, requestLocation, clearLocation } = useUserLocation();

  // Tabs slider scroll state
  const tabsScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateTabScrollState = useCallback(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const scrollTabs = (direction) => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const delta = Math.max(160, Math.floor(el.clientWidth * 0.7));
    el.scrollBy({ left: direction * delta, behavior: 'smooth' });
  };

  useEffect(() => {
    updateTabScrollState();
    const el = tabsScrollRef.current;
    if (!el) return;
    const onScroll = () => updateTabScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateTabScrollState);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateTabScrollState);
    };
  }, [updateTabScrollState]);

  // Scroll active tab into view when it changes
  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const active = el.querySelector(`[data-testid="tab-${activeTab}"]`);
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [activeTab]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Fetch entities
      let entityType = null;
      if (activeTab === 'outfitters_guides') entityType = 'outfitters_guides';
      else if (activeTab === 'industry_partners') entityType = 'industry_partners';
      else if (activeTab === 'campgrounds_resorts') entityType = 'campgrounds_resorts';
      else if (activeTab === 'manufacturers') entityType = 'manufacturers';
      else if (activeTab === 'rv_partners') entityType = 'rv_partners';
      else if (activeTab === 'retailers') entityType = 'retailers';
      else if (activeTab === 'conservancies_land_trusts') entityType = 'conservancies_land_trusts';
      else if (activeTab === 'state_parks_dnr') entityType = 'state_parks_dnr';
      else if (activeTab === 'summer_camps') entityType = 'summer_camps';
      else if (activeTab === 'concessionaire') entityType = 'concessionaire';
      else if (activeTab === 'outdoor_education') entityType = 'outdoor_education';
      else if (activeTab === 'tdas') entityType = 'tdas';

      // In map view, the search bar performs geocoding (handled in handleSearch);
      // it should NOT also filter the entity list by text. So we skip the search
      // param when viewMode === 'map' and the user is geocoding.
      const entitySearch = viewMode === 'map' ? undefined : (searchQuery || undefined);

      // In map view we want ALL geocoded entities (up to a sensible cap) so
      // pins aren't truncated; list view paginates client-side from this fetch.
      const fetchLimit = viewMode === 'map' ? 1000 : 50;

      const entitiesResponse = await axios.get(`${API}/entities`, {
        params: { entity_type: entityType, search: entitySearch, limit: fetchLimit },
        headers,
        withCredentials: true
      });
      setEntities(entitiesResponse.data.entities || []);

      // Fetch events if needed
      if (activeTab === 'all' || activeTab === 'event') {
        const eventsResponse = await axios.get(`${API}/events`, {
          headers,
          withCredentials: true
        });
        setEvents(eventsResponse.data.events || []);
      } else {
        setEvents([]);
      }
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchQuery, token, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    // Update view mode from URL parameter
    const view = searchParams.get('view');
    if (view === 'map') {
      setViewMode('map');
    }
  }, [searchParams]);

  // Geocode a place name via the backend proxy to /api/geocode (avoids CORS + complies with Nominatim ToS)
  const geocodeLocation = async (query) => {
    const resp = await axios.get(`${API}/geocode`, {
      params: { q: query, limit: 1 },
    });
    const results = resp.data?.results || [];
    if (results.length === 0) return null;
    return results[0];
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const query = searchQuery.trim();

    // In map mode, treat the search as a location (place / city / state / region)
    if (viewMode === 'map' && query.length > 0) {
      setGeocoding(true);
      try {
        const result = await geocodeLocation(query);
        if (result) {
          // Choose zoom level based on the matched feature type
          const zoom = ['country', 'continent'].includes(result.type) ? 5
            : ['state', 'region'].includes(result.type) ? 7
            : ['county'].includes(result.type) ? 9
            : ['city', 'town', 'village'].includes(result.type) ? 11
            : 13;
          setMapFlyTo({ lat: result.lat, lng: result.lng, zoom, label: result.label });
          toast.success(`Showing area around ${result.label.split(',').slice(0, 2).join(',').trim()}`);
        } else {
          toast.error(`No location found for "${query}"`);
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        toast.error('Location search failed. Please try again.');
      } finally {
        setGeocoding(false);
      }
      return;
    }

    // Default: refilter entities (grid view)
    fetchData();
  };

  // Clear the geocoded fly-to target whenever user switches tabs or changes view mode
  useEffect(() => {
    setMapFlyTo(null);
  }, [activeTab, viewMode]);

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams(value !== 'all' ? { type: value } : {});
  };

  const tabs = [
    { value: 'all', label: 'All', icon: SlidersHorizontal },
    { value: 'campgrounds_resorts', label: 'Campgrounds/Resorts', icon: Tent },
    { value: 'concessionaire', label: 'Concessionaire', icon: Building2 },
    { value: 'conservancies_land_trusts', label: 'Conservancies/Land Trusts', icon: Trees },
    { value: 'industry_partners', label: 'Industry Partners', icon: Building2 },
    { value: 'manufacturers', label: 'Manufacturers', icon: ShoppingBag },
    { value: 'outdoor_education', label: 'Outdoor Education', icon: Backpack },
    { value: 'outfitters_guides', label: 'Outfitters/Guides', icon: Users },
    { value: 'retailers', label: 'Retailers', icon: ShoppingBag },
    { value: 'rv_partners', label: 'RV Partners', icon: ShoppingBag },
    { value: 'state_parks_dnr', label: 'State Parks/DNR', icon: Landmark },
    { value: 'summer_camps', label: 'Summer Camps', icon: Sun },
    { value: 'tdas', label: 'TDAs', icon: Building2 },
    { value: 'event', label: 'Events', icon: Calendar },
  ];

  const filteredEntities = entities.filter(e => {
    // Search query filter — skip in map mode (search is interpreted as a location to fly to, not an entity filter)
    if (searchQuery && viewMode !== 'map') {
      const matchesSearch = e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           e.description?.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
    }
    
    // Tags filter
    if (selectedTags.length > 0) {
      const entityTags = e.tags || [];
      const hasMatchingTag = selectedTags.some(tag => 
        entityTags.map(t => t.toLowerCase()).includes(tag.toLowerCase())
      );
      if (!hasMatchingTag) return false;
    }
    
    // Specialties filter (for guides)
    if (selectedSpecialties.length > 0 && e.specialties) {
      const hasMatchingSpecialty = selectedSpecialties.some(specialty =>
        e.specialties.map(s => s.toLowerCase()).includes(specialty.toLowerCase())
      );
      if (!hasMatchingSpecialty) return false;
    }
    
    // Years of experience filter (for guides)
    if (e.years_experience !== undefined) {
      if (e.years_experience < minExperience || e.years_experience > maxExperience) {
        return false;
      }
    }

    // "Near Me" filter: drop entities without coordinates so the distance
    // sort can produce a meaningful ordering.
    if (userCoords && !(e.latitude && e.longitude)) {
      return false;
    }

    return true;
  });

  // Haversine distance in km between two {lat,lng} points. Used by "Near Me".
  const haversineKm = (a, b) => {
    const toRad = (d) => (d * Math.PI) / 180;
    const R = 6371; // mean earth radius in km
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  // When the user has opted in to "Near Me", sort by distance ascending.
  const sortedEntities = userCoords
    ? [...filteredEntities]
        .map((e) => ({
          ...e,
          _distance_km: haversineKm(userCoords, { lat: e.latitude, lng: e.longitude }),
        }))
        .sort((a, b) => a._distance_km - b._distance_km)
    : filteredEntities;

  const filteredEvents = events.filter(e => {
    if (!searchQuery) return true;
    return e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
           e.description?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-background" data-testid="discovery-page">
      <Navigation />
      
      <main className="px-4 pt-[68px] pb-8 sm:pt-20 sm:pb-12 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-5 sm:mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1 sm:mb-2">Discover</h1>
              <p className="text-sm sm:text-lg text-muted-foreground">
                Find guides, organizations, campgrounds, and more
              </p>
            </div>
            <Button
              onClick={() => navigate('/route-planner')}
              className="rounded-full gap-2 self-start"
              size="sm"
              data-testid="route-planner-btn"
            >
              <Route className="h-4 w-4" />
              Plan a Route
            </Button>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-5 sm:mb-8">
            <div className="flex gap-2 sm:gap-3">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                <Input
                  placeholder={viewMode === 'map' ? 'Search a city, state, or region...' : 'Search guides, camps, events...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-11 h-11 sm:h-12 rounded-full text-sm sm:text-base"
                  data-testid="discovery-search-input"
                />
              </div>

              {/* Advanced Filters */}
              <DiscoveryFiltersSheet
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                selectedSpecialties={selectedSpecialties}
                setSelectedSpecialties={setSelectedSpecialties}
                minExperience={minExperience}
                setMinExperience={setMinExperience}
                maxExperience={maxExperience}
                setMaxExperience={setMaxExperience}
              />

              {/* Near Me — requests location ON DEMAND when first tapped. */}
              <Button
                type="button"
                variant={userCoords ? 'default' : 'outline'}
                size="icon"
                className="h-11 w-11 sm:h-12 sm:w-12 rounded-full shrink-0"
                onClick={() => (userCoords ? clearLocation() : requestLocation())}
                disabled={locState === 'requesting'}
                title={userCoords ? 'Clear location sort' : 'Sort by distance from me'}
                data-testid="near-me-btn"
              >
                <Locate className={`h-4 w-4 sm:h-5 sm:w-5 ${locState === 'requesting' ? 'animate-pulse' : ''}`} />
              </Button>
              
              {/* View Toggle */}
              <div className="hidden sm:flex rounded-full border border-border bg-card p-1">
                <Button
                  type="button"
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="rounded-full"
                  onClick={() => setViewMode('grid')}
                  data-testid="view-grid-btn"
                >
                  <LayoutGrid className="h-5 w-5" />
                </Button>
                <Button
                  type="button"
                  variant={viewMode === 'map' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="rounded-full"
                  onClick={() => setViewMode('map')}
                  data-testid="view-map-btn"
                >
                  <Map className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Mobile-only view toggle (full-width segmented control) */}
            <div className="mt-3 grid grid-cols-2 gap-1 rounded-full border border-border bg-card p-1 sm:hidden" role="group" aria-label="View mode">
              <Button
                type="button"
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-full gap-1.5"
                onClick={() => setViewMode('grid')}
                data-testid="view-grid-btn-mobile"
              >
                <LayoutGrid className="h-4 w-4" />
                List
              </Button>
              <Button
                type="button"
                variant={viewMode === 'map' ? 'secondary' : 'ghost'}
                size="sm"
                className="rounded-full gap-1.5"
                onClick={() => setViewMode('map')}
                data-testid="view-map-btn-mobile"
              >
                <Map className="h-4 w-4" />
                Map
              </Button>
            </div>
          </form>

          {/* Near-Me active indicator */}
          {userCoords && (
            <div
              className="mb-4 sm:mb-5 inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/20 px-3 py-1.5 text-xs sm:text-sm"
              data-testid="near-me-indicator"
            >
              <Locate className="h-3.5 w-3.5 text-primary" />
              <span className="text-foreground">
                Sorted by distance · <span className="font-semibold">{sortedEntities.length}</span> nearby
              </span>
              <button
                type="button"
                onClick={clearLocation}
                className="ml-1 text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                data-testid="near-me-clear-btn"
              >
                clear
              </button>
            </div>
          )}

          {/* Tabs */}
          <DiscoveryTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabsScrollRef={tabsScrollRef}
            canScrollLeft={canScrollLeft}
            canScrollRight={canScrollRight}
            scrollTabs={scrollTabs}
          />

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : viewMode === 'map' ? (
            /* Map View */
            <DiscoveryMapView
              filteredEntities={sortedEntities}
              activeTab={activeTab}
              tabs={tabs}
              isAdmin={isAdmin}
              mapFlyTo={mapFlyTo}
              setMapFlyTo={setMapFlyTo}
              setSearchQuery={setSearchQuery}
            />
          ) : (
            /* Grid View */
            <DiscoveryGridView
              filteredEntities={sortedEntities}
              filteredEvents={filteredEvents}
              activeTab={activeTab}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default Discovery;
